import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef } from "react"

// This higher-order component listens to any click events
// outside its (wrapped) children. For optimal performance
// use shouldListen prop to limit active listeners.

const setRef = (ref, value) => {
  if (!ref) {
    return
  }
  if (typeof ref === "function") {
    ref(value)
    return
  }
  ref.current = value
}

const OutsideClickWrapper = ({
  children,
  mouseEvent = "mousedown",
  shouldListen = true,
  onOutsideClick
}) => {
  const nodeRef = useRef(null)
  const child = React.Children.only(children)

  const handleClick = useCallback(
    (e) => {
      const node = nodeRef.current
      if (node && e.target !== node && !node.contains(e.target)) {
        if (onOutsideClick) {
          onOutsideClick(e)
        }
      }
    },
    [onOutsideClick]
  )

  useEffect(() => {
    if (!shouldListen) {
      return
    }
    document.body.addEventListener(mouseEvent, handleClick)
    document.body.addEventListener("touchend", handleClick)
    return () => {
      document.body.removeEventListener(mouseEvent, handleClick)
      document.body.removeEventListener("touchend", handleClick)
    }
  }, [handleClick, mouseEvent, shouldListen])

  return React.cloneElement(child, {
    ref: (node) => {
      nodeRef.current = node
      setRef(child.ref, node)
    }
  })
}

OutsideClickWrapper.displayName = "OutsideClickWrapper"

OutsideClickWrapper.propTypes = {
  children: PropTypes.element.isRequired,
  mouseEvent: PropTypes.string,
  shouldListen: PropTypes.bool,
  onOutsideClick: PropTypes.func
}

export default OutsideClickWrapper
